import { useEffect, useState, useRef, lazy, Suspense, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from "../../../../../hooks/reduxHooks";
import { ChatMsgI } from '../../../../../types/fetch/chatRoom';
import { useAppDispatch } from "../../../../../hooks/reduxHooks";
import { UserI } from '../../../../../types/user';
import { setCurrentTool, setChatRoomSessionName, setIsChatRoomDocViewerOpen } from '../../../../../state/features/app/appSlice';
import { ToolEnum } from '../../../../../enums/app';
import useFetchChatRoomWebSockets from '../../../../../hooks/useFetchChatRoomWebSockets';
import { MsgTypeEnum, ResponseStatusEnum } from '../../../../../enums/fetch/chatRoom';
import { OrganizationI } from '../../../../../types/organization';
import { classNames } from '../../../../../utils/styleHelpers';
import { MultiSourceViewerDataI } from '../../../../../types/ui/multiSourceViewer';
import SkeletonLoader from '../../../../../components/Loaders/SkeletonLoader';
import { trackEvent } from '../../../../../utils/tracking';
import Lottie from 'react-lottie';
import { PROMPT_MSG } from '../../../../../constants/fetch/chatRoom';
import ResponseContainer from '../../../../../components/Chats/ResponseContainer';
import SkeletonOfChatInput from '../../../../../components/Chats/Skeletons/SkeletonOfChatInput';
import SensoLoader from '../../../../../components/Loaders/SensoLoader';

const ChatResponse = lazy(() => import('./components/ChatResponse/ChatResponse'));
const ChatInput = lazy(() => import('./components/ChatInput/ChatInput'));
const MultiSourceViewer = lazy(() => import('../../../../../components/MultiSourceViewer/MultiSourceViewer'));
const SuggestedQuestions = lazy(() => import('../../../../../components/Chats/SuggestedQuestions'));
const Sessions = lazy(() => import('./components/Sessions'));
const UserQuery = lazy(() => import('../../../../../components/Chats/UserQuery'));
const ProgressIndicator = lazy(() => import('../../../../../components/Chats/ProgressIndicator'));
const UserAvatar = lazy(() => import('../../../../../components/Avatars/UserAvatar'));
const ChatRoomHeader = lazy(() => import('./components/ChatRoomHeader'));

const FetchChatRoom = () => {
    const [inputMsg, setInputMsg] = useState<string>('');
    const [scrollIntoEndOfChat, setScrollIntoEndOfChat] = useState<boolean>(false);
    const [multiSourceViewerData, setMultiSourceViewerData] = useState<MultiSourceViewerDataI | undefined>(undefined);
    const [isMultiSourceViewerOpen, setIsMultiSourceViewerOpen] = useState<boolean>(false);
    const [isMetaDataPanelExpanded, setIsMetaDataPanelExpanded] = useState(false);
    const [inputMsgType, setInputMsgType] = useState<`${MsgTypeEnum}` | null>(null);

    const endOfChatRef = useRef<HTMLElement>(null);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const userHasScrolledUpRef = useRef<boolean>(false);

    const location = useLocation();
    const dispatch = useAppDispatch();
    const selectedOrg: OrganizationI | undefined = useAppSelector((state) => state.organization.selectedOrganization);
    const currentUser: UserI | undefined = useAppSelector((state) => state.user.currentUser);
    const isChatRoomDocViewerOpen: boolean | undefined = useAppSelector((state) => state.app.isChatRoomDocViewerOpen);
    const { connectToHistoricalSession, sessionId, sessionName, setFilterCollectionIds, sendMsg, topQuestions, isPageLoading,
        chatMsgsMap, isLastMsgResponseNotComplete, stopGeneratingAnswer, undoAnyMsgFeedback, updateAnyMsgFeedback } = useFetchChatRoomWebSockets(true); // Explicitly set isActive to true for this component

    useEffect(() => {
        dispatch(setCurrentTool(ToolEnum.CHAT_ROOM));

        // Clear session name from header when component unmounts
        return () => {
            dispatch(setChatRoomSessionName(undefined));
        };
    }, []);

    useEffect(() => {
        setInputMsg('');
    }, [sessionId]);

    // Update header with session name when it changes
    useEffect(() => {
        if (sessionName) {
            dispatch(setChatRoomSessionName(sessionName));
        }
    }, [sessionName]);

    // Update header when document viewer opens/closes
    useEffect(() => {
        dispatch(setIsChatRoomDocViewerOpen(isMultiSourceViewerOpen));
    }, [isMultiSourceViewerOpen]);

    // Sync Redux state back to local state (when header Back button is clicked)
    useEffect(() => {
        if (isChatRoomDocViewerOpen === false && isMultiSourceViewerOpen) {
            setIsMultiSourceViewerOpen(false);
        }
    }, [isChatRoomDocViewerOpen]);

    useEffect(() => {
        if (endOfChatRef.current) {
            endOfChatRef.current.scrollIntoView({ behavior: "auto" });
            setScrollIntoEndOfChat(false);
        }
    }, [scrollIntoEndOfChat, isPageLoading]);

    // Helper function to check if user is near the bottom of the scroll container
    const isNearBottom = useCallback(() => {
        if (!chatContainerRef.current) return true;
        const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
        // Consider "near bottom" if within 100px of the bottom
        return scrollHeight - scrollTop - clientHeight < 100;
    }, []);

    // Handle scroll events to detect when user scrolls up
    const handleScroll = useCallback(() => {
        if (!chatContainerRef.current) return;

        if (isNearBottom()) {
            // User scrolled back to bottom, re-enable auto-scroll
            userHasScrolledUpRef.current = false;
        } else if (isLastMsgResponseNotComplete) {
            // User scrolled up while response is generating
            userHasScrolledUpRef.current = true;
        }
    }, [isNearBottom, isLastMsgResponseNotComplete]);

    // Add scroll event listener to chat container
    useEffect(() => {
        const container = chatContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    // Reset scroll tracking when a new message is sent (new question)
    useEffect(() => {
        const msgCount = Object.keys(chatMsgsMap).length;
        if (msgCount > 0) {
            // When a new message is added, reset scroll tracking and scroll to bottom
            userHasScrolledUpRef.current = false;
            if (endOfChatRef.current) {
                endOfChatRef.current.scrollIntoView({ behavior: "smooth" });
            }
        }
    }, [Object.keys(chatMsgsMap).length]);

    // Auto-scroll while answer is being generated (only if user hasn't scrolled up)
    useEffect(() => {
        if (isLastMsgResponseNotComplete && endOfChatRef.current) {
            const scrollInterval = setInterval(() => {
                if (endOfChatRef.current && !userHasScrolledUpRef.current) {
                    endOfChatRef.current.scrollIntoView({ behavior: "smooth" });
                }
            }, 500);

            return () => clearInterval(scrollInterval);
        }
    }, [isLastMsgResponseNotComplete]);

    useEffect(() => {
        if (location.state?.chat && !isPageLoading) {
            sendMsg(location.state.chat.msg, location.state.chat.msgType, location.state.chat.collectionIds);
        }
    }, [location.state?.chat, isPageLoading]);

    const handleInputMsgSubmit = (msgType?: `${MsgTypeEnum}`) => {
        sendMsg(inputMsg, msgType);
        setInputMsgType(msgType || null);
        setInputMsg('');
    }

    const handleSuggestedQuestionSelect = (question: string) => {
        // Track the event
        trackEvent('Suggested Question Clicked', {
            question: question
        });

        // Call the original sendMsg function
        sendMsg(question);
    };

    const handleStopGeneratingAnswer = () => {
        stopGeneratingAnswer();
    }

    const renderQuestion = (id: number, question: string,) => {
        return (
            <div key={id} className='flex flex-row items-center pb-2 md:pb-5'>
                <div className='flex items-center min-w-[32px] min-h-[32px] !p-0'>
                    <Suspense fallback={<></>}>
                        <UserAvatar currentUser={currentUser} className='!w-8 !h-8' initialsClassName='!text-[14px] md:!text-[12px]' />
                    </Suspense>
                </div>
                <div className='flex'>
                    <span className='flex bg-gray-50 p-2 md:p-3 rounded-lg ml-2 md:ml-3 w-auto text-gray-900'>
                        {question}
                    </span>
                </div>
            </div>
        );
    }

    const SkeletonOfSuggestedQuestion = () => {
        return (
            <>
                <div className='mb-10'></div>
                <div className="flex items-center w-full mb-10">
                    <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-32"></div>
                    <div className="h-2.5 ms-2 bg-gray-200 rounded-full dark:bg-gray-700 w-24"></div>
                </div>
                <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-40 mb-10"></div>
                <div className="flex items-center w-full mb-10">
                    <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-32"></div>
                    <div className="h-2.5 ms-2 bg-gray-200 rounded-full dark:bg-gray-700 w-24"></div>
                </div>
                <div className="flex items-center w-full mb-10">
                    <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-32"></div>
                    <div className="h-2.5 ms-2 bg-gray-200 rounded-full dark:bg-gray-700 w-24"></div>
                </div>
                <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[280px] mb-8"></div>
            </>
        )
    };

    return (
        <div className='w-full h-full flex flex-col'>
            {isPageLoading ? (
                <div className='flex items-center justify-center h-screen w-full'>
                    <SensoLoader />
                </div>
            ) : (
                <div className='w-full h-full flex'>
                    <div className={classNames('hidden flex-col flex-shrink-0 lg:w-[317px] border-r border-b light-scroll-bar overflow-auto', !isMultiSourceViewerOpen ? 'lg:flex' : 'lg:hidden')}>
                        <Suspense fallback={<></>}>
                            <Sessions currentSessionName={sessionName} currentSessionId={sessionId} connectToHistoricalSession={connectToHistoricalSession} />
                        </Suspense>
                    </div>
                    <>
                        <div className={classNames('h-full flex flex-col', isMultiSourceViewerOpen && isMetaDataPanelExpanded ? 'w-[calc(100%/2-300px)]' : isMultiSourceViewerOpen && !isMetaDataPanelExpanded ? 'w-1/2' : 'w-[calc(100%-317px)]')}>
                            {/* Check if there are no messages in the chat:
                             * 1. chatMsgsMap is empty (no existing messages)
                             * 2. When we are navigating from Global Chat input we have chat msg so we don't show the empty state
                             */}
                            {Object.keys(chatMsgsMap).length === 0 && !location.state?.chat?.msg ? (
                                <div ref={chatContainerRef} className='flex flex-col h-full px-6 md:px-12 lg:px-16 bg-gradient-to-b from-slate-50/50 to-white overflow-y-auto'>
                                    {/* Content Container - Positioned near top */}
                                    <div className='flex flex-col max-w-4xl w-full mx-auto pt-6 md:pt-10'>
                                        {/* Header - Logo and Title on same row */}
                                        <div className='flex flex-col items-center text-center mb-8'>
                                            <div className='flex items-center justify-center gap-4 mb-3'>
                                                {selectedOrg?.logo && (
                                                    <img
                                                        src={selectedOrg.logo}
                                                        alt={`${selectedOrg.name} logo`}
                                                        className='w-12 h-12 md:w-14 md:h-14 object-contain rounded-xl'
                                                    />
                                                )}
                                                <h1 className='text-2xl md:text-3xl lg:text-4xl font-extrabold text-slate-800'>
                                                    Welcome to <span className='text-primary-color'>{selectedOrg?.name}</span>
                                                </h1>
                                            </div>
                                            <p className='text-slate-500 text-base md:text-lg font-medium'>Ask anything about your documents and knowledge base</p>
                                        </div>

                                        {/* Search Bar - Full Width */}
                                        <div className='w-full mb-8'>
                                            <Suspense fallback={<SkeletonLoader skeleton={SkeletonOfChatInput} />}>
                                                <ChatInput
                                                    inputMsg={inputMsg}
                                                    setInputMsg={setInputMsg}
                                                    initialFilterCollectionIds={location.state?.chat?.collectionIds}
                                                    setFilterCollectionIds={setFilterCollectionIds}
                                                    isLastMsgResponseNotComplete={isLastMsgResponseNotComplete}
                                                    isMultiSourceViewerOpen={isMultiSourceViewerOpen}
                                                    handleInputMsgSubmit={handleInputMsgSubmit}
                                                    handleStopGeneratingAnswer={handleStopGeneratingAnswer}
                                                    isEmptyState={true}
                                                />
                                            </Suspense>
                                        </div>

                                        {/* Quick Questions - Stacked and Full Width */}
                                        {topQuestions.length > 0 && (
                                            <div className='w-full'>
                                                <Suspense fallback={<SkeletonLoader skeleton={SkeletonOfSuggestedQuestion} />}>
                                                    {!isPageLoading ? (
                                                        <div className='flex flex-col items-center'>
                                                            <span className='text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4'>Try asking</span>
                                                            <div className='flex flex-col gap-3 w-full'>
                                                                {topQuestions.slice(0, 3).map((question, index) => (
                                                                    <button
                                                                        key={index}
                                                                        onClick={() => handleSuggestedQuestionSelect(question)}
                                                                        className='w-full px-5 py-4 bg-white border border-slate-200 rounded-xl text-sm text-slate-600 hover:border-primary-color hover:text-primary-color hover:bg-primary-color/5 transition-all duration-200 text-left font-medium shadow-sm hover:shadow-md'
                                                                    >
                                                                        {question}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ) : <SkeletonOfSuggestedQuestion />}
                                                </Suspense>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div ref={chatContainerRef} className={classNames('w-full flex-1 px-4 overflow-y-auto light-scroll-bar', !isMultiSourceViewerOpen ? 'md:px-28 ' : 'md:px-5')}>
                                        <div className={classNames('pt-[20px]', !isMultiSourceViewerOpen ? 'md:pt-[50px]' : 'md:pt-[20px]')}>
                                            <Suspense fallback={<></>}>
                                                <ChatRoomHeader isHidden={!isMultiSourceViewerOpen} sessionName={sessionName} handleNaviagetBack={() => setIsMultiSourceViewerOpen(false)} />
                                            </Suspense>
                                            <ResponseContainer>
                                                <span className='flex flex-col w-auto'>
                                                    {PROMPT_MSG}
                                                </span>
                                            </ResponseContainer>
                                            {Object.values(chatMsgsMap).map((chatMsg: ChatMsgI, index) => (
                                                <div key={index} className='justify-items-start'>
                                                    <Suspense fallback={<></>}>
                                                        <UserQuery id={index} query={chatMsg.content.question} />
                                                    </Suspense>
                                                    <Suspense fallback={<></>}>
                                                        <ProgressIndicator isResponseComplete={chatMsg.status === ResponseStatusEnum.RESPONSE_COMPLETE} status={chatMsg.status} />
                                                    </Suspense>
                                                    <div className='flex items-center justify-items-start !w-full'>
                                                        <Suspense fallback={<></>}>
                                                            <ChatResponse
                                                                inputMsgType={inputMsgType}
                                                                chatMsg={chatMsg}
                                                                sendMsg={sendMsg}
                                                                isLastMsgResponseNotComplete={isLastMsgResponseNotComplete}
                                                                setScrollIntoEndOfChat={setScrollIntoEndOfChat}
                                                                undoAnyMsgFeedback={undoAnyMsgFeedback}
                                                                updateAnyMsgFeedback={updateAnyMsgFeedback}
                                                                setMultiSourceViewerData={setMultiSourceViewerData}
                                                                setIsMultiSourceViewerOpen={setIsMultiSourceViewerOpen} />
                                                        </Suspense>
                                                    </div>
                                                </div>)
                                            )}
                                            {/* Use to scroll to end of chat, after generating answers */}
                                            <span ref={endOfChatRef}></span>
                                        </div>
                                    </div>
                                    <div className={classNames('px-4 pb-4', !isMultiSourceViewerOpen ? 'md:px-28 ' : 'md:px-5')}>
                                        <Suspense fallback={<SkeletonLoader skeleton={SkeletonOfChatInput} />}>
                                            <ChatInput
                                                inputMsg={inputMsg}
                                                setInputMsg={setInputMsg}
                                                initialFilterCollectionIds={location.state?.chat?.collectionIds}
                                                setFilterCollectionIds={setFilterCollectionIds}
                                                isLastMsgResponseNotComplete={isLastMsgResponseNotComplete}
                                                isMultiSourceViewerOpen={isMultiSourceViewerOpen}
                                                handleInputMsgSubmit={handleInputMsgSubmit}
                                                handleStopGeneratingAnswer={handleStopGeneratingAnswer}
                                            />
                                        </Suspense>
                                    </div>
                                </>
                            )}
                        </div>
                        {isMultiSourceViewerOpen &&
                            <div className={classNames('flex h-[calc(100%-20px)]', isMetaDataPanelExpanded ? 'w-[calc(100%/2+300px)]' : 'w-1/2')}>
                                <Suspense fallback={<></>}>
                                    <MultiSourceViewer
                                        key={multiSourceViewerData!.parentId}
                                        multiSourceViewerData={multiSourceViewerData!}
                                        metadataPanelConfig={{
                                            isMetaDataPanelExpanded,
                                            setIsMetaDataPanelExpanded
                                        }}
                                        showDocumentsNavigationPanel={false}
                                        handleViewerClose={() => setIsMultiSourceViewerOpen(false)}
                                    />
                                </Suspense>
                            </div>
                        }
                    </>
                </div>
            )}
        </div>
    )
}

export default FetchChatRoom;
